import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { QuoteRequestApi } from '../../api/adminApi';
import './QuoteRequestsAdmin.css';

const QUOTE_STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'closed', label: 'Closed' }
];

const formatDate = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

const QuoteRequestsAdmin = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [statusDraft, setStatusDraft] = useState('new');
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusNotice, setStatusNotice] = useState('');

  const loadRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await QuoteRequestApi.getAll();
      setRequests(Array.isArray(data) ? data : []);
      if (selectedRequest) {
        const updated = data.find((item) => item.id === selectedRequest.id);
        setSelectedRequest(updated || null);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to load quote requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedRequest) {
      setStatusDraft(selectedRequest.status || 'new');
      setStatusNotice('');
    }
  }, [selectedRequest]);

  const handleStatusChange = (event) => {
    setStatusDraft(event.target.value);
    setStatusNotice('');
  };

  const handleStatusUpdate = async () => {
    if (!selectedRequest) return;
    const currentStatus = selectedRequest.status || 'new';
    if (statusDraft === currentStatus) {
      return;
    }
    setStatusUpdating(true);
    setError('');
    setStatusNotice('');
    try {
      const updated = await QuoteRequestApi.updateStatus(selectedRequest.id, statusDraft);
      setRequests((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setSelectedRequest(updated);
      setStatusNotice('Status updated.');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const renderTable = () => {
    if (loading) {
      return <p>Loading quote requests…</p>;
    }
    if (!requests.length) {
      return <p>No quote requests yet.</p>;
    }
    return (
      <table className="quotes-table">
        <thead>
          <tr>
            <th>Requester</th>
            <th>Service</th>
            <th>App Type</th>
            <th>Contact Method</th>
            <th>Status</th>
            <th>Received</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((request) => {
            const isSelected = selectedRequest?.id === request.id;
            return (
              <React.Fragment key={request.id}>
                <tr>
                  <td>
                    <div className="cell-stack">
                      <strong>{request.name}</strong>
                      <span>
                        {request.email}
                        {request.phone ? ` · ${request.phone}` : ''}
                      </span>
                    </div>
                  </td>
                  <td>{request.service}</td>
                  <td>{request.appType || '—'}</td>
                  <td>
                    <span className="contact-method-pill">{request.contactMethod}</span>
                  </td>
                  <td>
                    <span className={`status-pill status-${(request.status || 'new').toLowerCase()}`}>
                      {request.status || 'new'}
                    </span>
                  </td>
                  <td>{formatDate(request.createdAt)}</td>
                  <td>
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => {
                        if (isSelected) {
                          setSelectedRequest(null);
                          setStatusNotice('');
                        } else {
                          setSelectedRequest(request);
                        }
                      }}
                    >
                      {isSelected ? 'Hide brief' : 'View brief'}
                    </button>
                  </td>
                </tr>
                {isSelected && selectedRequest && (
                  <tr className="quote-detail-row">
                    <td colSpan="7">
                      <div className="quote-detail-card">
                        <div className="quote-detail-header">
                          <div>
                            <h3>{selectedRequest.service}</h3>
                            <p>
                              {selectedRequest.name} · {selectedRequest.email}
                              {selectedRequest.phone ? ` · ${selectedRequest.phone}` : ''}
                            </p>
                            <p className="quote-detail-meta">
                              Prefers {selectedRequest.contactMethod} · {selectedRequest.source || 'request-quote-modal'} · {formatDate(selectedRequest.createdAt)}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="admin-secondary-btn"
                            onClick={() => {
                              setSelectedRequest(null);
                              setStatusNotice('');
                            }}
                          >
                            Close
                          </button>
                        </div>
                        <div className="status-form">
                          <label htmlFor="quote-status">Status</label>
                          <div className="status-form-row">
                            <select
                              id="quote-status"
                              className="status-select"
                              value={statusDraft}
                              onChange={handleStatusChange}
                              disabled={statusUpdating}
                            >
                              {QUOTE_STATUS_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="admin-primary-btn"
                              onClick={handleStatusUpdate}
                              disabled={statusUpdating || statusDraft === (selectedRequest.status || 'new')}
                            >
                              {statusUpdating ? 'Updating…' : 'Save status'}
                            </button>
                          </div>
                          {statusNotice && <p className="status-notice success">{statusNotice}</p>}
                        </div>
                        <dl className="quote-detail-list">
                          <div>
                            <dt>Application Type</dt>
                            <dd>{selectedRequest.appType || 'Not specified'}</dd>
                          </div>
                        </dl>
                        <div className="quote-detail-message">
                          <h4>Project Details</h4>
                          <p>{selectedRequest.projectDetails}</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <AdminLayout
      title="Quote Requests"
      description="Centralize every quote submission from the marketing site."
      actions={(
        <button type="button" className="admin-secondary-btn" onClick={loadRequests} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      )}
    >
      {error && <div className="admin-alert">{error}</div>}
      <div className="quotes-table-wrapper">{renderTable()}</div>
    </AdminLayout>
  );
};

export default QuoteRequestsAdmin;
